import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '../utils/api';
import './DashboardHome.css';

const API_BASE = process.env.REACT_APP_API_BASE || '';

function getTimeAgo(date) {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
}

function DashboardHome({ currentUser }) {
  const [stats, setStats] = useState([
    { label: 'Total Dogs', value: 'Loading...' },
    { label: 'Missing Dogs', value: 'Loading...' },
    { label: 'High Risk', value: 'Loading...' },
    { label: 'Active Streams', value: '1' },
  ]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [fallenCount, setFallenCount] = useState(null);
  const [editingFallen, setEditingFallen] = useState(false);
  const [fallenDraft, setFallenDraft] = useState('');
  const [savingFallen, setSavingFallen] = useState(false);

  const [notSeenFrom, setNotSeenFrom] = useState('');
  const [notSeenCount, setNotSeenCount] = useState(null);
  const [editingNotSeenFrom, setEditingNotSeenFrom] = useState(false);
  const [notSeenFromDraft, setNotSeenFromDraft] = useState('');
  const [savingNotSeenFrom, setSavingNotSeenFrom] = useState(false);

  useEffect(() => {
    const fetchCounters = async () => {
      try {
        const resp = await api.get('/api/wall-counters');
        setFallenCount(resp.data?.fallen_this_year ?? 0);
        setNotSeenCount(resp.data?.not_seen_month ?? 0);
        setNotSeenFrom(resp.data?.not_seen_month_from_date || '');
      } catch (err) {
        console.error('Could not fetch wall counters:', err);
        setFallenCount(0);
        setNotSeenCount(0);
        setNotSeenFrom('');
      }
    };
    fetchCounters();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsResponse, missingResponse] = await Promise.all([
          axios.get(`${API_BASE}/api/stats`),
          axios.get(`${API_BASE}/api/missing-dogs`)
        ]);
        
        const statsData = statsResponse.data;
        const missingCount = missingResponse.data?.total || 0;
        
        if (statsResponse.status === 200 && statsData) {
          setStats([
            { label: 'Total Dogs', value: statsData.total_dogs?.toLocaleString() || '0' },
            { label: 'Missing Dogs', value: missingCount.toString() },
            { label: 'High Risk', value: statsData.high_risk_dogs?.toString() || '0' },
            { label: 'Active Streams', value: '1' },
          ]);
        } else {
          throw new Error('Invalid response from stats endpoint');
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        console.error('Error details:', error.response?.data || error.message);
        setStats([
          { label: 'Total Dogs', value: 'Error' },
          { label: 'Missing Dogs', value: 'Error' },
          { label: 'High Risk', value: 'Error' },
          { label: 'Active Streams', value: '1' },
        ]);
      }
    };

    const fetchRecentActivity = async () => {
      try {
        setActivityLoading(true);
        const response = await axios.get(`${API_BASE}/api/recent-activity?limit=10`);
        if (response.status === 200 && response.data?.activities) {
          setRecentActivity(response.data.activities);
        }
      } catch (error) {
        console.error('Error fetching recent activity:', error);
        setRecentActivity([]);
      } finally {
        setActivityLoading(false);
      }
    };

    fetchStats();
    fetchRecentActivity();
  }, []);

  const saveFallenCount = async () => {
    const value = parseInt(fallenDraft, 10);
    if (isNaN(value) || value < 0) {
      alert('Please enter a non-negative integer.');
      return;
    }
    setSavingFallen(true);
    try {
      const resp = await api.put('/api/settings/manual_fallen_count', { value });
      setFallenCount(resp.data?.value ?? value);
      setEditingFallen(false);
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not save');
    } finally {
      setSavingFallen(false);
    }
  };

  const saveNotSeenFrom = async () => {
    const value = (notSeenFromDraft || '').trim();
    setSavingNotSeenFrom(true);
    try {
      const resp = await api.put('/api/settings/not_seen_month_from_date', { value });
      const saved = resp.data?.value ?? (value || null);
      setNotSeenFrom(saved || '');
      setEditingNotSeenFrom(false);
      // Re-fetch so the count reflects the new scope
      try {
        const cr = await api.get('/api/wall-counters');
        setNotSeenCount(cr.data?.not_seen_month ?? 0);
      } catch (_) { /* ignore */ }
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not save');
    } finally {
      setSavingNotSeenFrom(false);
    }
  };

  const quickActions = [
    { path: '/staff/dogs', label: 'Manage Dogs', description: 'Add, edit, or view dog profiles' },
    { path: '/staff/risk-matrix', label: 'Settings', description: 'Configure risk matrix settings and default dog filters' },
    { path: '/staff/stream', label: 'Live Streams', description: 'View and manage video feeds' },
  ];

  return (
    <div className="dashboard-home">
      <div className="welcome-section">
        <h2>Welcome to the Management System</h2>
        <p>Monitor and manage greyhound information, risk assessments, and live streams from one central dashboard.</p>
      </div>

      <div className="stats-grid">
        {stats.map((stat) => {
          return (
            <div key={stat.label} className="stat-card">
              <div className="stat-content">
                <div className="stat-label">{stat.label}</div>
                <div className="stat-value">{stat.value}</div>
              </div>
            </div>
          );
        })}
        <div className="stat-card fallen-card">
          <div className="stat-content">
            <div className="stat-card-header">
              <div className="stat-label">Fallen on Track (This Year)</div>
              {!editingFallen && currentUser?.role !== 'viewer' && (
                <button
                  type="button"
                  className="fallen-edit-btn"
                  onClick={() => { setEditingFallen(true); setFallenDraft(String(fallenCount ?? 0)); }}
                  aria-label="Edit counter"
                >
                  ✎
                </button>
              )}
            </div>
            {editingFallen ? (
              <div className="fallen-edit">
                <input
                  type="number"
                  min="0"
                  value={fallenDraft}
                  onChange={(e) => setFallenDraft(e.target.value)}
                />
                <div className="fallen-edit-actions">
                  <button type="button" disabled={savingFallen} onClick={saveFallenCount}>
                    {savingFallen ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" onClick={() => setEditingFallen(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="stat-value">{fallenCount ?? '—'}</div>
            )}
          </div>
        </div>

        <div className="stat-card fallen-card">
          <div className="stat-content">
            <div className="stat-card-header">
              <div className="stat-label">Not Seen Over 1 Month — From Date</div>
              {!editingNotSeenFrom && currentUser?.role !== 'viewer' && (
                <button
                  type="button"
                  className="fallen-edit-btn"
                  onClick={() => { setEditingNotSeenFrom(true); setNotSeenFromDraft(notSeenFrom || ''); }}
                  aria-label="Edit not-seen-from date"
                >
                  ✎
                </button>
              )}
            </div>
            {editingNotSeenFrom ? (
              <div className="fallen-edit">
                <input
                  type="date"
                  value={notSeenFromDraft}
                  onChange={(e) => setNotSeenFromDraft(e.target.value)}
                />
                <div className="fallen-edit-hint">
                  Limits the wall's "not seen for over one month" counter to dogs whose last race falls on or after this date. Leave empty to count all-time.
                </div>
                <div className="fallen-edit-actions">
                  <button type="button" disabled={savingNotSeenFrom} onClick={saveNotSeenFrom}>
                    {savingNotSeenFrom ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" onClick={() => setEditingNotSeenFrom(false)}>Cancel</button>
                  {notSeenFromDraft && (
                    <button
                      type="button"
                      onClick={() => setNotSeenFromDraft('')}
                      style={{ marginLeft: 'auto' }}
                    >Clear</button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="stat-value">{notSeenCount ?? '—'}</div>
                <div className="stat-sub">
                  {notSeenFrom
                    ? <>Counting from <strong>{notSeenFrom}</strong> → today</>
                    : <>Counting <strong>all-time</strong> (no lower bound)</>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          {quickActions.map((action) => {
            return (
              <Link key={action.path} to={action.path} className="action-card">
                <div className="action-content">
                  <h4>{action.label}</h4>
                  <p>{action.description}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {activityLoading ? (
            <div className="activity-item">
              <div className="activity-content">
                <p>Loading recent activity...</p>
              </div>
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="activity-item">
              <div className="activity-content">
                <p>No recent activity to display</p>
              </div>
            </div>
          ) : (
            recentActivity.map((activity, index) => {
              const timestamp = activity.timestamp ? new Date(activity.timestamp) : null;
              const timeAgo = timestamp ? getTimeAgo(timestamp) : 'Recently';
              
              return (
                <div key={index} className="activity-item">
                  <div className="activity-content">
                    <p><strong>{activity.type}:</strong> {activity.description}</p>
                    <span className="activity-time">{timeAgo}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardHome;

